import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAccordionButton } from "react-bootstrap";
import {
  BsChevronDown,
  BsDropletFill,
  BsShieldCheck,
  BsGeoAltFill,
  BsTruck,
  BsAwardFill,
  BsBuildingsFill,
  BsPatchCheckFill,
} from "react-icons/bs";

// Variable System Tokens mapped directly inside local style block for fluid architecture
const UI_TOKENS = {
  brandDeepBlue: "#1a3c6b",
  brandCyan: "#36bfa8",
  glassBackground: "rgba(255, 255, 255, 0.72)",
  glassBorder: "rgba(226, 232, 240, 0.8)",
  blurStrength: "20px",
};

function SystemToggle({ children, id, activeId, onTrigger }) {
  const customClick = useAccordionButton(id, () => onTrigger && onTrigger(id));
  const isOpen = activeId === id;

  return (
    <button
      type="button"
      onClick={customClick}
      className="d-flex justify-content-between align-items-center w-100 border-0 bg-transparent py-4 text-start px-2"
      style={{ outline: "none", cursor: "pointer" }}
    >
      <span
        className="fw-bold m-0 transition-all text-dark"
        style={{
          letterSpacing: "-0.4px",
          fontSize: "1.1rem",
          color: isOpen ? `${UI_TOKENS.brandDeepBlue} !important` : "#1e293b",
        }}
      >
        {children}
      </span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 16 }}
        className="d-flex align-items-center justify-content-center rounded-circle"
        style={{
          width: "36px",
          height: "36px",
          backgroundColor: isOpen
            ? UI_TOKENS.brandDeepBlue
            : "rgba(0,0,0,0.03)",
          color: isOpen ? "#ffffff" : "#64748b",
        }}
      >
        <BsChevronDown size={13} />
      </motion.div>
    </button>
  );
}

const FAQPage = () => {
  const [activeId, setActiveId] = useState("faq_0");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title =
      "Frequently Asked Questions | Corporate Index | NGO Waters";
  }, []);

  const handleToggle = (id) => {
    setActiveId(activeId === id ? null : id);
  };

  // Structured Data Matrix mapped with permanent string identifiers instead of raw numeric indices
  const schemaFaQs = [
    {
      id: "faq_0",
      question: "What is NGO Waters?",
      answer:
        "NGO Waters is the certified structural packaged drinking water distribution framework under PONIA FOOD AND BEVERAGE PRIVATE LIMITED. It is engineered to establish secure access to clean processing setups while preserving competitive community metrics.",
    },
    {
      id: "faq_1",
      question: "How does NGO Waters ensure compliance and quality parameters?",
      answer:
        "Our automated plant structures maintain continuous technical testing operations. This covers advanced Reverse Osmosis (RO), absolute UV exposure grids, and automated multi-stage carbon filtering to fulfill regulatory guidelines set by the Bureau of Indian Standards (BIS) and FSSAI.",
    },
    {
      id: "faq_2",
      question:
        "What specific volume models and pricing matrices are available?",
      answer:
        "Our operations process four precise volume configurations calibrated for retail deployment and wholesale logistics:",
      isGrid: true,
      gridItems: [
        { metric: "250ml", value: "₹6 Retail Value" },
        { metric: "500ml", value: "₹10 Retail Value" },
        { metric: "1L (1000ml)", value: "₹20 Retail Value" },
        { metric: "2L (2000ml)", value: "₹30 Retail Value" },
      ],
    },
    {
      id: "faq_3",
      question:
        "What geographic sectors fall within your immediate distribution range?",
      answer:
        "The immediate supply grid focuses directly on the Mathura-Vrindavan development blocks [Uttar Pradesh, India] utilizing real-time dispatch systems for high-volume networks, commercial hubs, and retail outlets.",
    },
    {
      id: "faq_4",
      question: "How can institutional clients log bulk order requests?",
      answer:
        "Wholesale structures and commercial event planners can log systemic metrics via our explicit Bulk Order Interface. These entries route straight to our local dispatch controllers for scheduling verification.",
    },
    {
      id: "faq_5",
      question: "Are logistical transport services complimentary?",
      answer:
        "Complimentary logistical tracking and plant delivery operations apply to high-volume commercial schedules or contract-backed regional supplies as detailed in our system terms.",
    },
    {
      id: "faq_6",
      question:
        "What parameter tracks exist for regional distribution contracts?",
      answer:
        "PONIA FOOD AND BEVERAGE PRIVATE LIMITED allocates sector-exclusive geographical supply layers and optimization matrices for verified distribution hubs. Onboarding forms can be processed on our central communications panel.",
    },
    {
      id: "faq_7",
      question:
        "Can corporate and hospitality sectors initiate continuous supply SLAs?",
      answer:
        "Yes, we construct dedicated service-level agreements (SLAs) for institutional offices, hospitality providers, and industrial centers with long-term margin benefits.",
    },
    {
      id: "faq_8",
      question:
        "What distinct operational parameters separate NGO Waters from alternative lines?",
      answer:
        "Our model excels because of our direct plant optimization mechanisms:",
      isList: true,
      listItems: [
        {
          title: "Optimized Industrial Base",
          desc: "Lean pricing architectures that deliver premium filtration assets safely.",
          icon: <BsBuildingsFill className="text-primary" />,
        },
        {
          title: "Continuous Quality Audits",
          desc: "Rigorous testing schedules managed internally at our production plants.",
          icon: <BsShieldCheck className="text-success" />,
        },
        {
          title: "Direct Geolocation Logistics",
          desc: "Shorter shipping legs ensuring fast dispatch speeds in the Mathura-Vrindavan zones.",
          icon: <BsGeoAltFill className="text-danger" />,
        },
      ],
    },
  ];

  return (
    <div
      className="w-100 py-5 px-3 px-lg-5 position-relative border-0"
      style={{
        fontFamily: "'Inter', sans-serif",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Structural Semantic SEO Data Injector Block */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: schemaFaQs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        })}
      </script>

      {/* Fluid Decorative Glass Underlays */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "600px",
          height: "600px",
          background: "rgba(26, 60, 107, 0.02)",
          top: "-100px",
          right: "-100px",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "500px",
          height: "500px",
          background: "rgba(54, 191, 216, 0.03)",
          bottom: "5%",
          left: "-100px",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />

      {/* Header Radial Area */}
      <header
        className="mx-auto text-center mb-5 position-relative z-1"
        style={{ maxWidth: "800px" }}
      >
        <div className="d-inline-flex align-items-center gap-2 px-3 py-1.5 mb-3 bg-white border rounded-pill shadow-xs">
          <BsPatchCheckFill className="text-primary" size={13} />
          <span
            className="fw-semibold text-muted tracking-wide text-uppercase"
            style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}
          >
            Corporate Registry Index
          </span>
        </div>
        <h1
          className="fw-extrabold text-dark tracking-tight mb-3"
          style={{ fontSize: "calc(1.8rem + 1.2vw)", letterSpacing: "-1.5px" }}
        >
          Frequently Asked Questions
        </h1>
        <p
          className="text-secondary small mx-auto mb-0"
          style={{ maxWidth: "600px", lineHeight: "1.6" }}
        >
          Verify systematic configurations regarding water purification
          processing lines, regional logistical metrics, and distribution
          operations.
        </p>
      </header>

      {/* Next-Gen Glassmorphic Card Block */}
      <main
        className="mx-auto position-relative z-1"
        style={{ maxWidth: "900px" }}
      >
        <motion.div
          initial={{ y: 25, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 90, damping: 17 }}
          className="border-0 p-3 p-md-4"
          style={{
            borderRadius: "24px",
            backgroundColor: UI_TOKENS.glassBackground,
            backdropFilter: `blur(${UI_TOKENS.blurStrength})`,
            WebkitBackdropFilter: `blur(${UI_TOKENS.blurStrength})`,
            border: `1px solid ${UI_TOKENS.glassBorder}`,
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.03)",
          }}
        >
          <div className="accordion" id="corporate_faq_matrix">
            {schemaFaQs.map((item) => {
              const isSelected = activeId === item.id;
              return (
                <div
                  key={item.id}
                  className="mx-2 transition-all"
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <SystemToggle
                    id={item.id}
                    activeId={activeId}
                    onTrigger={handleToggle}
                  >
                    {item.question}
                  </SystemToggle>

                  <AnimatePresence initial={false}>
                    {isSelected && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div
                          className="pb-4 text-secondary small px-2"
                          style={{ lineHeight: "1.6", color: "#475569" }}
                        >
                          <p className="m-0">{item.answer}</p>

                          {/* Sizing Matrix Sub-Grid Rendering */}
                          {item.isGrid && (
                            <div className="row g-2 mt-3 pt-1">
                              {item.gridItems.map((grid, gIdx) => (
                                <div key={gIdx} className="col-6 col-sm-3">
                                  <div className="p-3 rounded-3 text-center border bg-white shadow-xs">
                                    <div className="fw-bold text-dark small">
                                      {grid.metric}
                                    </div>
                                    <div
                                      className="fw-semibold mt-1"
                                      style={{
                                        color: UI_TOKENS.brandDeepBlue,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {grid.value}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Differentiating Factors List Rendering */}
                          {item.isList && (
                            <div className="d-flex flex-column gap-3 mt-4 pt-2">
                              {item.listItems.map((list, lIdx) => (
                                <div
                                  key={lIdx}
                                  className="d-flex align-items-start gap-3 p-2.5 rounded-3 bg-white border"
                                >
                                  <div className="p-2 bg-light rounded d-flex align-items-center justify-content-center">
                                    {list.icon}
                                  </div>
                                  <div>
                                    <h6 className="m-0 fw-bold text-dark small mb-0.5">
                                      {list.title}
                                    </h6>
                                    <p
                                      className="m-0 text-muted ultra-small"
                                      style={{ fontSize: "0.78rem" }}
                                    >
                                      {list.desc}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      </main>

      {/* External Reference Link Row */}
      <section className="text-center mt-5 position-relative z-1">
        <p className="text-muted small m-0" style={{ fontSize: "0.88rem" }}>
          Could not locate your specific integration parameter?
          <a
            href="/askquestion"
            className="fw-bold text-decoration-none ms-1.5"
            style={{ color: UI_TOKENS.brandDeepBlue }}
          >
            File a direct system ticket →
          </a>
        </p>
      </section>

      {/* True Legal Entity Verification Footer Layer */}
      <footer
        className="w-100 mt-5 pt-4 text-center border-top text-muted position-relative z-1"
        style={{
          fontSize: "0.7rem",
          letterSpacing: "0.3px",
          borderColor: "rgba(0,0,0,0.05)",
        }}
      >
        <div className="fw-semibold text-dark mb-1">
          PONIA FOOD AND BEVERAGE PRIVATE LIMITED
        </div>
        <div>
          CIN: U11043UP2024PTC213517 • CORPORATE REGISTRY SYSTEM DATA
          REPLICATION
        </div>
        <div
          className="mt-2 text-uppercase font-monospace text-muted"
          style={{ fontSize: "0.65rem", opacity: 0.6 }}
        >
          Pipeline Framework Managed Under Signature Handle:
          web_developer_hariom
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
